export type CodeComponentTemplateBranding = {
  bodyFontFamily?: string;
  headingFontFamily?: string;
  accentColor?: string;
  accentTextColor?: string;
  textColor?: string;
  surfaceColor?: string;
  secondaryColor?: string;
  secondaryTextColor?: string;
  buttonFontFamily?: string;
  buttonTextTransform?: string;
  buttonLetterSpacing?: string;
  buttonRadius?: string;
};

// Serializes branding into the source once; templates read it through CSS
// variables so the primary color prop can still override the accent.
export function createTemplateTheme(
  {
    bodyFontFamily,
    headingFontFamily,
    accentColor,
    accentTextColor,
    textColor,
    surfaceColor,
    secondaryColor,
    secondaryTextColor,
    buttonFontFamily,
    buttonTextTransform,
    buttonLetterSpacing,
    buttonRadius,
  }: CodeComponentTemplateBranding,
  defaultAccentColor: string
) {
  const hasAccent = Boolean(accentColor && accentTextColor);
  const theme: Record<string, string> = {};
  if (bodyFontFamily) theme.fontFamily = bodyFontFamily;
  if (headingFontFamily) theme['--template-heading-font'] = headingFontFamily;
  if (hasAccent) theme['--template-accent-text'] = accentTextColor!;
  if (textColor) theme['--template-ink'] = textColor;
  if (surfaceColor) theme['--template-surface'] = surfaceColor;
  if (secondaryColor && secondaryTextColor) {
    theme['--template-accent-secondary'] = secondaryColor;
    theme['--template-accent-secondary-text'] = secondaryTextColor;
  }
  if (buttonFontFamily) theme['--template-button-font'] = buttonFontFamily;
  if (buttonTextTransform)
    theme['--template-button-case'] = buttonTextTransform;
  if (buttonLetterSpacing) {
    theme['--template-button-tracking'] = buttonLetterSpacing;
  }
  if (buttonRadius) theme['--template-button-radius'] = buttonRadius;
  return {
    themeSource: `/* ── brand theme ── */
// Every template shares these colors: accent (+ hover and text on it),
// accent-secondary (the brand's second accent, or the accent itself),
// secondary (a soft accent tint), ink for text, muted for secondary text,
// surface for backgrounds, and border/subtle derived from ink on the surface.
const THEME = ${JSON.stringify(theme)};
const HEADING_FONT = "[font-family:var(--template-heading-font,inherit)]";
// Buttons take the site button's font, letter case, and letter spacing.
const BUTTON_TEXT = "[font-family:var(--template-button-font,inherit)] [text-transform:var(--template-button-case,none)] [letter-spacing:var(--template-button-tracking,normal)]";
const FOCUS_RING = "focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--template-accent)]";

// Shared type scale: edit it here to restyle every heading and paragraph.
const TYPE = {
  h1: "text-3xl font-semibold text-[color:var(--template-ink)] " + HEADING_FONT,
  h2: "text-2xl font-semibold text-[color:var(--template-ink)] " + HEADING_FONT,
  h3: "text-base font-semibold text-[color:var(--template-ink)] " + HEADING_FONT,
  body: "text-base text-[color:var(--template-ink)]",
  small: "text-sm text-[color:var(--template-muted)]",
};

function themeStyle(primaryColor): import("react").CSSProperties {
  return {
    "--template-ink": "#0f172a",
    "--template-surface": "#ffffff",
    "--template-accent-secondary": "var(--template-accent)",
    "--template-accent-secondary-text": "var(--template-accent-text, white)",
    ...THEME,
    "--template-accent": primaryColor,
    "--template-accent-hover": "color-mix(in srgb, var(--template-accent) 85%, black)",
    "--template-secondary": "color-mix(in srgb, var(--template-accent) 14%, var(--template-surface))",
    "--template-muted": "color-mix(in srgb, var(--template-ink) 70%, var(--template-surface))",
    "--template-border": "color-mix(in srgb, var(--template-ink) 12%, var(--template-surface))",
    "--template-border-strong": "color-mix(in srgb, var(--template-ink) 20%, var(--template-surface))",
    "--template-subtle": "color-mix(in srgb, var(--template-ink) 4%, var(--template-surface))",
  } as import("react").CSSProperties;
}`,
    primaryColor: JSON.stringify(hasAccent ? accentColor : defaultAccentColor),
  };
}
